import { PaymentFrequency } from '@admiin-com/ds-graphql';
import {
  WBBox,
  WBFlex,
  WBCheckbox,
  WBTypography,
  WBTextFieldV2,
  WBSelectV2,
  WBToggleButtonGroup,
  WBRoundedToggleButton,
  WBInformationIcon,
} from '@admiin-com/ds-web';
import { InputLabel, useMediaQuery, useTheme, MenuItem } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import React, { useEffect } from 'react';
import { TASK_FORM_FIELDS } from '../../../helpers/form';
import InvoiceFrequencyDatePicker from '../../DatePicker/InvoiceFrequencyDatePicker';
import { DATE_FORMATS } from '@admiin-com/ds-common';
import { DateTime } from 'luxon';

// Helper function to calculate the last payment date based on frequency and number of payments
const calculateLastPaymentDate = (
  startDate: string,
  frequency: string,
  numberOfPayments: number
): string => {
  if (!startDate || !numberOfPayments) return '';

  try {
    const start = DateTime.fromFormat(startDate, DATE_FORMATS.BACKEND_DATE);
    if (!start.isValid) return '';

    let endDate: DateTime;
    switch (frequency) {
      case 'WEEKLY':
        endDate = start.plus({ weeks: numberOfPayments - 1 });
        break;
      case 'FORTNIGHTLY':
        endDate = start.plus({ weeks: (numberOfPayments - 1) * 2 });
        break;
      case 'MONTHLY':
        endDate = start.plus({ months: numberOfPayments - 1 });
        break;
      case 'QUARTERLY':
        endDate = start.plus({ months: (numberOfPayments - 1) * 3 });
        break;
      case 'ANNUALLY':
        endDate = start.plus({ years: numberOfPayments - 1 });
        break;
      default:
        endDate = start.plus({ months: numberOfPayments - 1 });
        break;
    }

    return endDate.toFormat(DATE_FORMATS.DUE_DATE);
  } catch (error) {
    return '';
  }
};

// Helper function to get period description text
const getFrequencyPeriodDescription = (
  frequency: string,
  dueDate: string
): string => {
  if (!dueDate) return '';

  try {
    const start = DateTime.fromFormat(dueDate, DATE_FORMATS.BACKEND_DATE);
    if (!start.isValid) return '';

    switch (frequency) {
      case 'WEEKLY':
        return `${start.toFormat('dd MMM yyyy')} - ${start
          .plus({ weeks: 1 })
          .toFormat('dd MMM yyyy')}`;
      case 'FORTNIGHTLY':
        return `${start.toFormat('dd MMM yyyy')} - ${start
          .plus({ weeks: 2 })
          .toFormat('dd MMM yyyy')}`;
      case 'MONTHLY':
        return `${start.toFormat('dd MMM yyyy')} - ${start
          .plus({ months: 1 })
          .toFormat('dd MMM yyyy')}`;
      case 'QUARTERLY':
        return `${start.toFormat('dd MMM yyyy')} - ${start
          .plus({ months: 3 })
          .toFormat('dd MMM yyyy')}`;
      case 'ANNUALLY':
        return `${start.toFormat('dd MMM yyyy')} - ${start
          .plus({ years: 1 })
          .toFormat('dd MMM yyyy')}`;
      default:
        return '';
    }
  } catch (error) {
    return '';
  }
};

export const InvoicePaymentFrequency = () => {
  const { t } = useTranslation();

  const methods = useFormContext();
  const { watch, setValue } = methods;
  const paymentFrequency = watch('paymentFrequency');
  const recurringType = watch('recurringType') || 'never';
  const dueDate = watch('dueAt') || '';
  const numberOfPayments = watch('numberOfPayments') || ''; // Changed from 0 to empty string

  // Effect to clear values when switching between recurring types
  useEffect(() => {
    if (recurringType === 'count') {
      // Clear end date when switching to number of payments
      setValue('endAt', '', { shouldDirty: true, shouldValidate: true });
    } else if (recurringType === 'date') {
      // Clear number of payments when switching to end date
      setValue('numberOfPayments', '', {
        shouldDirty: true,
        shouldValidate: true,
      });
    } else if (recurringType === 'never') {
      // Clear both when selecting never
      setValue('numberOfPayments', 0, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue('endAt', '', { shouldDirty: true, shouldValidate: true });
    }
  }, [recurringType, setValue]);

  // Calculate last payment date for the description
  const lastPaymentDate = React.useMemo(
    () => calculateLastPaymentDate(dueDate, paymentFrequency, numberOfPayments),
    [dueDate, paymentFrequency, numberOfPayments]
  );

  // Get period description for the selected frequency
  const frequencyPeriodDescription = React.useMemo(
    () => getFrequencyPeriodDescription(paymentFrequency, dueDate),
    [paymentFrequency, dueDate]
  );

  const inputs = React.useMemo(
    () => ({
      paymentFrequency: {
        ...TASK_FORM_FIELDS.paymentFrequency,
      },
      paymentTypes: {
        ...TASK_FORM_FIELDS.paymentTypes,
      },
      allowExtendedTerms: {
        ...TASK_FORM_FIELDS.allowExtendedTerms,
      },
      maximumInstallments: {
        ...TASK_FORM_FIELDS.maximumInstallments,
      },
      numberOfPayments: {
        ...TASK_FORM_FIELDS.numberOfPayments,
      },
      recurringType: {
        ...TASK_FORM_FIELDS.recurringType,
      },
      endAt: {
        ...TASK_FORM_FIELDS.endAt,
      },
    }),
    []
  );
  const isMobile = useMediaQuery(useTheme().breakpoints.down('sm'));
  return (
    <>
      <WBBox mt={2}>
        <Controller
          name={inputs.paymentFrequency.name}
          control={methods.control}
          defaultValue={PaymentFrequency.ONCE}
          render={({ field }) => (
            <WBToggleButtonGroup
              {...field}
              size="medium"
              fullWidth={isMobile}
              exclusive
              sx={{ gap: 1 }}
              label={t('paymentFrequency', { ns: 'taskbox' })}
            >
              {[PaymentFrequency.ONCE, PaymentFrequency.MONTHLY].map(
                (key: PaymentFrequency) => {
                  return (
                    <WBRoundedToggleButton
                      value={key}
                      key={key}
                      size="small"
                      selected={
                        key === PaymentFrequency.ONCE
                          ? key === paymentFrequency
                          : paymentFrequency !== 'ONCE'
                      }
                    >
                      {t(key === PaymentFrequency.ONCE ? key : 'RECURRING', {
                        ns: 'taskbox',
                      })}
                    </WBRoundedToggleButton>
                  );
                }
              )}
            </WBToggleButtonGroup>
          )}
        />
      </WBBox>

      {paymentFrequency !== PaymentFrequency.ONCE && (
        <WBBox mt={1}>
          <WBTypography variant="body2" color="text.secondary">
            {t('futureInvoicesGeneratedNote', { ns: 'taskbox' })}
          </WBTypography>
        </WBBox>
      )}

      {paymentFrequency !== PaymentFrequency.ONCE && (
        <WBFlex mt={2}>
          <Controller
            name="paymentFrequency"
            control={methods.control}
            defaultValue={PaymentFrequency.MONTHLY}
            render={({ field }) => (
              <WBBox width={isMobile ? '100%' : '50%'} pr={isMobile ? 0 : 2}>
                <WBSelectV2
                  variant="standard"
                  fullWidth
                  label={t('invoiceEvery', { ns: 'taskbox' })}
                  value={field.value}
                  onChange={field.onChange}
                >
                  {Object.keys(PaymentFrequency)
                    .filter((key) => key !== PaymentFrequency.ONCE)
                    .map((key) => (
                      <MenuItem key={key} value={key}>
                        {t(`${key}_SHORT`, {
                          ns: 'taskbox',
                          defaultValue: t(key, { ns: 'taskbox' }),
                        })}
                      </MenuItem>
                    ))}
                </WBSelectV2>
              </WBBox>
            )}
          />
        </WBFlex>
      )}

      {paymentFrequency !== PaymentFrequency.ONCE &&
        frequencyPeriodDescription && (
          <WBBox mt={1}>
            <WBTypography variant="body2" color="text.secondary">
              {t('firstInvoicePeriod', {
                ns: 'taskbox',
                defaultValue: 'First invoice period',
              })}
              : {frequencyPeriodDescription}
            </WBTypography>
          </WBBox>
        )}

      {paymentFrequency !== PaymentFrequency.ONCE && (
        <WBBox mt={2}>
          <WBFlex
            flexDirection={isMobile ? 'column' : 'row'}
            alignItems="flex-start"
          >
            <WBBox width={isMobile ? '100%' : '50%'} pr={isMobile ? 0 : 2}>
              <Controller
                name={inputs.recurringType.name}
                control={methods.control}
                defaultValue="never"
                render={({ field }) => (
                  <WBSelectV2
                    variant="standard"
                    fullWidth
                    label={t('invoicesEnd', { ns: 'taskbox' })}
                    value={field.value}
                    onChange={field.onChange}
                  >
                    <MenuItem key="never" value="never">
                      {t('never', { ns: 'taskbox' })}
                    </MenuItem>
                    <MenuItem key="count" value="count">
                      {t('specifyNumberOfPayments', { ns: 'taskbox' })}
                    </MenuItem>
                    <MenuItem key="date" value="date">
                      {t('specifyEndDate', { ns: 'taskbox' })}
                    </MenuItem>
                  </WBSelectV2>
                )}
              />
            </WBBox>

            <WBBox
              width={isMobile ? '100%' : '50%'}
              pl={isMobile ? 0 : 2}
              mt={isMobile ? 2 : 0}
              display={recurringType === 'never' ? 'none' : 'block'}
            >
              {recurringType === 'count' ? (
                <Controller
                  name={inputs.numberOfPayments.name}
                  control={methods.control}
                  rules={inputs.numberOfPayments.rules}
                  defaultValue=""
                  render={({ field, fieldState }) => (
                    <WBTextFieldV2
                      {...field}
                      fullWidth
                      type="number"
                      label={t('numberOfInvoices', {
                        ns: 'taskbox',
                        defaultValue: 'Number of Invoices',
                      })}
                      placeholder="e.g. 6"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      inputProps={{ min: 2, max: 24 }}
                      value={field.value === 0 ? '' : field.value}
                    />
                  )}
                />
              ) : recurringType === 'date' ? (
                <Controller
                  key={`endAt-${recurringType}-${Math.random()}`} // Force controller to remount with new instance
                  name={inputs.endAt.name}
                  control={methods.control}
                  defaultValue={''}
                  rules={inputs.endAt.rules}
                  render={({ field, fieldState }) => {
                    return (
                      <InvoiceFrequencyDatePicker
                        value={field.value || ''}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        name={field.name}
                        label={t('endAt', { ns: 'taskbox' })}
                        placeholder={inputs.endAt.placeholder}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        variant="outlined"
                        showRecurringOptions={true}
                        startDate={dueDate}
                        frequency={paymentFrequency}
                      />
                    );
                  }}
                />
              ) : null}
            </WBBox>
          </WBFlex>

          {recurringType === 'never' && (
            <WBBox mt={1}>
              <WBTypography variant="body2" color="text.secondary">
                {t('neverRecurringNote', { ns: 'taskbox' })}
              </WBTypography>
            </WBBox>
          )}

          {recurringType === 'count' &&
            lastPaymentDate &&
            numberOfPayments > 0 &&
            dueDate && (
              <WBTypography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 1, display: 'block' }}
              >
                {t('lastPaymentDue', { ns: 'taskbox' })}: {lastPaymentDate}
              </WBTypography>
            )}
        </WBBox>
      )}

      {paymentFrequency === PaymentFrequency.ONCE && (
        <WBBox mt={2}>
          <Controller
            name={inputs.allowExtendedTerms.name}
            control={methods.control}
            defaultValue={false}
            render={({ field }) => (
              <>
                <InputLabel
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <WBTypography
                    component={'div'}
                    variant="inherit"
                    color={'inherit'}
                  >
                    {t('extendedPaymentTerms', { ns: 'taskbox' })}{' '}
                    <WBInformationIcon
                      title={t('extendedTermsInfo', { ns: 'taskbox' })}
                    />
                  </WBTypography>
                </InputLabel>
                <WBCheckbox
                  {...field}
                  checked={field.value}
                  label={t('allowExtendedTerms', { ns: 'taskbox' })}
                  disabled={paymentFrequency !== PaymentFrequency.ONCE}
                />
              </>
            )}
          />
        </WBBox>
      )}
    </>
  );
};
